import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

/**
 * AdminAuthProvider maintains a session for the Back-Office that is fully
 * independent from the B2C storefront login. Tokens are persisted under
 * `fa_admin_token` (vs. `fa_token` used by the B2C `AuthProvider`), so signing
 * out of the storefront never invalidates the admin session and vice-versa.
 *
 * The axios interceptor in `./api.js` route-scopes which token it sends based
 * on the current pathname (any `/admin/*` route uses the admin token).
 */
const AdminAuthContext = createContext(null);

export const ADMIN_TOKEN_KEY = "fa_admin_token";

export function AdminAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (_e) {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setUser(null);
  };

  return (
    <AdminAuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export const useAdminAuth = () => useContext(AdminAuthContext);
